import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CircleSlash } from 'lucide-react';
import type { EmrNotNeededKey } from '@his/shared';
import { put } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { tr } from '@/lib/i18n-core';
import { cn } from '@/lib/utils';
import { Switch } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { completionFactsOf } from './completion';
import type { Encounter } from './types';

function orderedCount(encounter: Encounter, key: EmrNotNeededKey): number {
  const facts = completionFactsOf(encounter);
  if (key === 'prescription') return facts.prescriptionsCount;
  if (key === 'lab_order') return facts.labOrdersCount;
  if (key === 'radiology_order') return facts.radiologyOrdersCount;
  return facts.procedureOrdersCount;
}

export function NotNeededToggle({ encounter, formKey, readOnly, required }: { encounter: Encounter; formKey: EmrNotNeededKey; readOnly: boolean; required: boolean }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const marks = encounter.emrNotNeeded ?? [];
  const marked = marks.includes(formKey);
  const save = useMutation({
    mutationFn: (on: boolean) => put(`/encounters/${encounter.id}`, { notNeeded: on ? [...new Set([...marks, formKey])] : marks.filter((key) => key !== formKey) }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['encounter', encounter.id] }),
    onError: (error) => toast.error(tr('opd', 'could_not_save'), describeError(error)),
  });
  if (!marked && orderedCount(encounter, formKey) > 0) return null;
  if (readOnly && !marked) return null;
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-2xl border px-4 py-3',
        marked ? 'border-success/30 bg-success/[0.06]' : required ? 'border-warning/40 bg-warning/[0.06]' : 'bg-card',
      )}
    >
      <CircleSlash className={cn('mt-0.5 h-4 w-4 shrink-0', marked ? 'text-success' : 'text-muted-foreground')} aria-hidden />
      <Switch
        className="min-w-0 flex-1"
        checked={marked}
        disabled={readOnly || save.isPending}
        onCheckedChange={(on) => save.mutate(on)}
        label={tr('opd', `emr_not_needed_${formKey}`)}
        description={required ? tr('opd', 'emr_not_needed_counts') : tr('opd', 'emr_not_needed_hint')}
      />
    </div>
  );
}
